import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import ImageCropper from './ImageCropper'
import RelationshipSelect from '../common/RelationshipSelect'

const {
  FiX,
  FiSave,
  FiUser,
  FiTrash2,
  FiCamera,
  FiAperture,
  FiHeart,
  FiPlus,
  FiSearch,
  FiUserPlus,
} = FiIcons

// Organized relationship options with proper grouping (flattened for HTML compatibility)
const relationshipGroups = [
  {
    name: "FAMILY",
    options: [
      // 1st Generation
      { type: 'parent', label: 'Parent (Father/Mother)', group: '1st Generation' },
      { type: 'child', label: 'Child (Son/Daughter)', group: '1st Generation' },
      { type: 'sibling', label: 'Sibling (Brother/Sister)', group: '1st Generation' },
      { type: 'spouse', label: 'Spouse (Husband/Wife)', group: '1st Generation' },
      { type: 'ex_spouse', label: 'Ex-Spouse (Ex-Husband/Ex-Wife)', group: '1st Generation' },
      { type: 'partner', label: 'Partner', group: '1st Generation' },
      { type: 'step_parent', label: 'Step-parent (Stepfather/Stepmother)', group: '1st Generation' },
      { type: 'step_child', label: 'Step-child (Stepson/Stepdaughter)', group: '1st Generation' },
      { type: 'step_sibling', label: 'Step-sibling (Stepbrother/Stepsister)', group: '1st Generation' },
      { type: 'half_sibling', label: 'Half-sibling (Half-brother/Half-sister)', group: '1st Generation' },
      { type: 'adoptive_parent', label: 'Adoptive Parent', group: '1st Generation' },
      { type: 'adoptive_child', label: 'Adopted Child', group: '1st Generation' },
      // 2nd Generation
      { type: 'grandparent', label: 'Grandparent (Grandfather/Grandmother)', group: '2nd Generation' },
      { type: 'grandchild', label: 'Grandchild (Grandson/Granddaughter)', group: '2nd Generation' },
      { type: 'parental_sibling', label: 'Parental Sibling (Aunt/Uncle)', group: '2nd Generation' },
      { type: 'sibling_child', label: 'Sibling\'s Child (Niece/Nephew)', group: '2nd Generation' },
      { type: 'great_grandparent', label: 'Great-Grandparent', group: '2nd Generation' },
      { type: 'great_grandchild', label: 'Great-Grandchild', group: '2nd Generation' },
      // 3rd Generation+
      { type: 'great_great_grandparent', label: 'Great-Great-Grandparent', group: '3rd Generation+' },
      { type: 'grandparent_sibling', label: 'Grandparent\'s Sibling (Great-aunt/Great-uncle)', group: '3rd Generation+' },
      { type: 'grandsibling_child', label: 'Grand-sibling\'s Child (Grandniece/Grandnephew)', group: '3rd Generation+' },
      { type: 'cousin', label: 'Cousin (1st Cousin)', group: '3rd Generation+' },
      { type: 'second_cousin', label: 'Second Cousin', group: '3rd Generation+' },
      { type: 'third_cousin', label: 'Third Cousin', group: '3rd Generation+' },
      { type: 'first_cousin_once_removed', label: 'First Cousin Once Removed', group: '3rd Generation+' },
      { type: 'first_cousin_twice_removed', label: 'First Cousin Twice Removed', group: '3rd Generation+' },
      { type: 'second_cousin_once_removed', label: 'Second Cousin Once Removed', group: '3rd Generation+' },
      // In-Laws
      { type: 'sibling_in_law', label: 'Sibling-in-law (Brother-in-law/Sister-in-law)', group: 'In-Laws' },
      { type: 'parent_in_law', label: 'Parent-in-law (Father-in-law/Mother-in-law)', group: 'In-Laws' },
      { type: 'child_in_law', label: 'Child-in-law (Son-in-law/Daughter-in-law)', group: 'In-Laws' },
      { type: 'cousin_in_law', label: 'Cousin-in-law', group: 'In-Laws' },
      { type: 'sibling_child_in_law', label: 'Niece-in-law / Nephew-in-law', group: 'In-Laws' },
      { type: 'parental_sibling_in_law', label: 'Aunt-in-law / Uncle-in-law', group: 'In-Laws' },
    ]
  },
  {
    name: "OTHER RELATIONSHIPS",
    options: [
      // Professional
      { type: 'colleague', label: 'Colleague', group: 'Professional' },
      { type: 'boss', label: 'Boss', group: 'Professional' },
      { type: 'employee', label: 'Employee', group: 'Professional' },
      // Personal
      { type: 'friend', label: 'Friend', group: 'Personal' }
    ]
  }
];

const ADD_NEW_PERSON_OPTION = '__add_new_person__'

const PersonForm = ({
  person,
  people,
  existingRelationships,
  allRelationships,
  onSubmit,
  onCancel,
  onAddNewPerson,
  onOpenPersonForm,
  initialName = '',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    sex: 'male',
    nicknames: '',
    notes: '',
    proximity: 'Medium',
    is_family: false,
    photoFile: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [cropperSource, setCropperSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  const fileInputRef = useRef(null);

  // State for inline relationship form
  const [newRelationships, setNewRelationships] = useState([
    { id: 1, relatedPersonId: '', relationshipType: '' },
  ]);
  const [nextRelId, setNextRelId] = useState(2);
  const [searchTerms, setSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState({});

  useEffect(() => {
    if (person) {
      setFormData({
        name: person.name || '',
        sex: person.sex || 'male',
        nicknames: person.nicknames || '',
        notes: person.notes || '',
        proximity: person.proximity || 'Medium',
        is_family: person.is_family || false,
        photoFile: null,
      })
      setPhotoPreview(person.photo_url || null)
      setShowRelationshipForm(false)
    } else {
      setFormData({
        name: initialName || '',
        sex: 'male',
        nicknames: '',
        notes: '',
        proximity: 'Medium',
        is_family: false,
        photoFile: null,
      })
      setPhotoPreview(null)
    }
  }, [person, initialName])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setCropperSource(event.target.result);
        setShowImageCropper(true);
      };
      reader.readAsDataURL(file);
      e.target.value = null;
    }
  };

  const handleCameraCapture = () => {
    setCropperSource('camera');
    setShowImageCropper(true);
  };

  const handleCroppedImageSave = (file, previewUrl) => {
    setFormData(prev => ({ ...prev, photoFile: file }));
    setPhotoPreview(previewUrl);
    setShowImageCropper(false);
    setCropperSource(null);
  };

  const handleRemovePhoto = () => {
    setFormData(prev => ({ ...prev, photoFile: null }));
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const validRelationships = newRelationships.filter(
        rel => rel.relatedPersonId && rel.relationshipType
      );
      await onSubmit({ ...formData, newRelationships: validRelationships });
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailablePeople = () => {
    if (!people) return [];
    const existingRelIds = new Set(
      (existingRelationships || []).map(rel =>
        rel.person_a_id === (person?.id || 'new') ? rel.person_b_id : rel.person_a_id
      )
    );
    return people.filter(p => p.id !== person?.id && !existingRelIds.has(p.id));
  };

  const availablePeople = getAvailablePeople();

  // --- Relationship Helpers ---
  const getPersonRelationships = (personId) => {
    if (!allRelationships) return [];
    return allRelationships
      .filter(rel => rel.person_a_id === personId || rel.person_b_id === personId)
      .map(rel => {
        const isPersonA = rel.person_a_id === personId;
        const otherPersonId = isPersonA ? rel.person_b_id : rel.person_a_id;
        const relationshipType = isPersonA ? rel.relationship_type_b : rel.relationship_type;
        const otherPerson = people.find(p => p.id === otherPersonId);
        return {
          personName: otherPerson?.name || 'Unknown',
          relationshipType: relationshipType || 'unknown'
        };
      })
      .slice(0, 2);
  };

  const formatRelationshipInfo = (relationships) => {
    if (!relationships || relationships.length === 0) return '';
    return relationships.map(rel => `${rel.personName}(${rel.relationshipType})`).join(', ');
  };

  // --- Relationship Form Handlers ---
  const handleSearch = (id, query) => {
    setSearchTerms(prev => ({ ...prev, [id]: query }));
    if (!query.trim()) {
      setSearchResults(prev => ({ ...prev, [id]: [] }));
      return;
    }
    const filtered = availablePeople.filter(
      p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        (p.nicknames && p.nicknames.toLowerCase().includes(query.toLowerCase()))
    );
    setSearchResults(prev => ({ ...prev, [id]: filtered }));
  };

  const handleAddNewRelationship = () => {
    setNewRelationships([
      ...newRelationships,
      { id: nextRelId, relatedPersonId: '', relationshipType: '' },
    ]);
    setNextRelId(nextRelId + 1);
  };

  const handleRemoveRelationship = (id) => {
    if (newRelationships.length > 1) {
      setNewRelationships(newRelationships.filter(rel => rel.id !== id));
    }
  };

  const handleRelationshipChange = (id, field, value) => {
    const updatedRelationships = newRelationships.map(rel =>
      rel.id === id ? { ...rel, [field]: value } : rel
    );
    setNewRelationships(updatedRelationships);
    if (field === 'relatedPersonId') {
      setSearchTerms(prev => ({ ...prev, [id]: '' }));
      setSearchResults(prev => ({ ...prev, [id]: [] }));
    }
  };

  const handleSelectPersonOption = async (id, value) => {
    if (value === ADD_NEW_PERSON_OPTION) {
      if (!onOpenPersonForm && !onAddNewPerson) {
        return;
      }

      const currentRelationship = newRelationships.find(rel => rel.id === id);
      const previousValue = currentRelationship?.relatedPersonId || '';

      // If onOpenPersonForm is provided, use it to open a full PersonForm
      if (onOpenPersonForm) {
        try {
          const newPerson = await onOpenPersonForm('');
          if (newPerson?.id) {
            handleRelationshipChange(id, 'relatedPersonId', newPerson.id);
          } else {
            setNewRelationships(prev =>
              prev.map(rel => (rel.id === id ? { ...rel, relatedPersonId: previousValue } : rel))
            );
          }
        } catch (error) {
          console.error('Failed to add new person:', error);
          setNewRelationships(prev =>
            prev.map(rel => (rel.id === id ? { ...rel, relatedPersonId: previousValue } : rel))
          );
        }
        return;
      }

      // Fallback to prompt-based quick add
      const defaultName = (searchTerms[id] || '').trim();
      const name = window.prompt('Enter the name for the new person', defaultName);

      if (!name) {
        setNewRelationships(prev =>
          prev.map(rel => (rel.id === id ? { ...rel, relatedPersonId: previousValue } : rel))
        );
        return;
      }

      try {
        const newPerson = await onAddNewPerson(name.trim());
        if (newPerson?.id) {
          handleRelationshipChange(id, 'relatedPersonId', newPerson.id);
        } else {
          setNewRelationships(prev =>
            prev.map(rel => (rel.id === id ? { ...rel, relatedPersonId: previousValue } : rel))
          );
        }
      } catch (error) {
        console.error('Failed to add new person:', error);
        setNewRelationships(prev =>
          prev.map(rel => (rel.id === id ? { ...rel, relatedPersonId: previousValue } : rel))
        );
      }
      return;
    }

    handleRelationshipChange(id, 'relatedPersonId', value);
  };

  const handleAddRelatedPerson = async (id, name) => {
    if (!onAddNewPerson) return
    const trimmedName = name.trim()
    if (!trimmedName) return
    try {
      const newPerson = await onAddNewPerson(trimmedName)
      if (newPerson?.id) {
        handleRelationshipChange(id, 'relatedPersonId', newPerson.id)
      }
    } catch (error) {
      console.error('Failed to add related person:', error)
    }
  }

  const handleAddPersonLink = async (id) => {
    // If onOpenPersonForm is provided, use it to open a full PersonForm
    if (onOpenPersonForm) {
      try {
        const newPerson = await onOpenPersonForm('');
        if (newPerson?.id) {
          handleRelationshipChange(id, 'relatedPersonId', newPerson.id);
        }
      } catch (error) {
        console.error('Failed to add new person:', error);
      }
      return;
    }

    // Fallback to prompt-based quick add
    if (!onAddNewPerson) return;
    
    const name = window.prompt('Enter the name for the new person');
    if (!name || !name.trim()) return;
    
    try {
      const newPerson = await onAddNewPerson(name.trim());
      if (newPerson?.id) {
        handleRelationshipChange(id, 'relatedPersonId', newPerson.id);
      }
    } catch (error) {
      console.error('Failed to add new person:', error);
    }
  };

  return (
    <>
      <motion.div
        key="person-form"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`bg-white rounded-lg shadow-lg p-6 w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ${!person && showRelationshipForm ? 'max-w-4xl' : 'max-w-lg'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">{person ? 'Edit Person' : 'Add New Person'}</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Cancel"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <div className="flex flex-col md:flex-row md:gap-6">
          {/* --- Left Column: Person Form --- */}
          <div className="flex-1 min-w-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <SafeIcon icon={FiUser} className="text-3xl text-gray-400" />
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="absolute -bottom-2 -right-2 flex space-x-1">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700" title="Upload photo">
                      <SafeIcon icon={FiAperture} size={16} />
                    </button>
                    <button type="button" onClick={handleCameraCapture} className="bg-green-600 text-white p-1.5 rounded-full hover:bg-green-700" title="Take photo">
                      <SafeIcon icon={FiCamera} size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex-grow space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Jane Doe" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
                    <select
                      name="sex"
                      value={formData.sex}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {photoPreview && (
                <div className="text-center">
                  <button type="button" onClick={handleRemovePhoto} className="text-xs text-red-500 hover:text-red-700 flex items-center mx-auto">
                    <SafeIcon icon={FiTrash2} className="mr-1" /> Remove Photo
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nicknames</label>
                <input type="text" name="nicknames" value={formData.nicknames} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Jenny, JD" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Any relevant notes..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proximity</label>
                <select name="proximity" value={formData.proximity} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" >
                  <option value="Close">Close</option>
                  <option value="Medium">Medium</option>
                  <option value="Far">Far</option>
                </select>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="is_family" name="is_family" checked={formData.is_family} onChange={handleChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label htmlFor="is_family" className="ml-2 block text-sm text-gray-900"> Is Family Member </label>
              </div>

              {!person && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md font-medium text-gray-800">Relationships</h3>
                    <button type="button" onClick={() => setShowRelationshipForm(prev => !prev)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" >
                      <SafeIcon icon={showRelationshipForm ? FiX : FiPlus} className="mr-1" />
                      {showRelationshipForm ? 'Cancel' : 'Add Relationship'}
                    </button>
                  </div>
                  {!showRelationshipForm && (
                    <p className="text-sm text-gray-500">Add relationships to connect this person to others.</p>
                  )}
                </div>
              )}

              <div className="mt-6 flex items-center justify-end space-x-3">
                <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300" >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center" >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <SafeIcon icon={FiSave} className="mr-2" />
                  )}
                  {loading ? 'Saving...' : 'Save Person'}
                </button>
              </div>
            </form>
          </div>

          {/* --- Right Column: Relationship Form --- */}
          <AnimatePresence>
            {!person && showRelationshipForm && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex-1 md:border-l md:pl-6 mt-6 md:mt-0 min-w-0"
              >
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <SafeIcon icon={FiUser} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium truncate">{formData.name || 'New Person'}</p>
                    <p className="text-sm text-gray-500">Is related to...</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {newRelationships.map((rel, index) => (
                    <div key={rel.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm text-gray-600">New Relationship #{index + 1}</p>
                        {newRelationships.length > 1 && (
                          <button type="button" onClick={() => handleRemoveRelationship(rel.id)} className="text-red-500 hover:text-red-700 p-1" >
                            <SafeIcon icon={FiTrash2} size={16} />
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Person</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SafeIcon icon={FiSearch} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={searchTerms[rel.id] || ''}
                            onChange={(e) => handleSearch(rel.id, e.target.value)}
                            placeholder="Search people..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="mt-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md">
                          {(searchResults[rel.id] || (searchTerms[rel.id] ? [] : availablePeople)).map(p => {
                            const relationships = getPersonRelationships(p.id);
                            const relationshipInfo = formatRelationshipInfo(relationships);
                            return (
                              <div
                                key={p.id}
                                className={`p-3 cursor-pointer hover:bg-blue-50 ${rel.relatedPersonId === p.id ? 'bg-blue-100' : ''}`}
                                onClick={() => handleRelationshipChange(rel.id, 'relatedPersonId', p.id)}
                              >
                                <div className="flex items-center space-x-2 min-w-0">
                                  <div className="font-medium truncate">{p.name}</div>
                                  {relationshipInfo && (
                                    <div className="text-xs text-purple-800 bg-purple-100 px-2 py-0.5 rounded-full whitespace-nowrap truncate">
                                      {relationshipInfo}
                                    </div>
                                  )}
                                </div>
                                {p.nicknames && <div className="text-sm text-gray-500 mt-1 truncate">({p.nicknames})</div>}
                              </div>
                            );
                          })}
                          {searchTerms[rel.id] && searchResults[rel.id]?.length === 0 && (
                            <div className="p-3 text-center text-gray-500">
                              <p>No results for "{searchTerms[rel.id]}"</p>
                              {onAddNewPerson && (
                                <button
                                  type="button"
                                  onClick={() => handleAddRelatedPerson(rel.id, searchTerms[rel.id])}
                                  className="mt-2 w-full text-left px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                  + Add "{searchTerms[rel.id]}" as a new person
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <select
                          value={rel.relatedPersonId}
                          onChange={(e) => handleSelectPersonOption(rel.id, e.target.value)}
                          className="w-full mt-2 pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="" disabled>Select a person</option>
                          {availablePeople.map(p => {
                            const relationshipInfo = formatRelationshipInfo(getPersonRelationships(p.id));
                            return <option key={p.id} value={p.id}>{p.name}{relationshipInfo && ` (${relationshipInfo})`}</option>
                          })}
                          {(onOpenPersonForm || onAddNewPerson) && (
                            <option value={ADD_NEW_PERSON_OPTION}>+ Add new person</option>
                          )}
                        </select>
                        {(onOpenPersonForm || onAddNewPerson) && (
                          <button
                            type="button"
                            onClick={() => handleAddPersonLink(rel.id)}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <SafeIcon icon={FiUserPlus} className="mr-1" />
                            Add new person
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Is their...</label>
                        <RelationshipSelect
                          value={rel.relationshipType}
                          onChange={(val) => handleRelationshipChange(rel.id, 'relationshipType', val)}
                          relationshipGroups={relationshipGroups}
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddNewRelationship}
                  className="mt-4 w-full text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md py-2 flex items-center justify-center"
                >
                  <SafeIcon icon={FiPlus} className="mr-2" />
                  Add Another Relationship
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      <AnimatePresence>
        {showImageCropper && (
          <ImageCropper
            imageUrl={cropperSource}
            onSave={handleCroppedImageSave}
            onCancel={() => {
              setShowImageCropper(false);
              setCropperSource(null);
            }}
            isCameraMode={cropperSource === 'camera'}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default PersonForm